import { useEffect, useState } from 'react';
import { IonAlert, IonButton, IonInput, IonItem, IonLabel, IonIcon, IonModal, IonToast, IonGrid, IonRow, IonCol, IonThumbnail, IonTextarea, IonDatetime, IonList, IonChip } from '@ionic/react';
import { getAllSalesActivities, addSalesActivity, updateSalesActivity, deleteSalesActivity } from '../services/sales.service';
import { addCircle, closeCircle, locationOutline, locationSharp } from 'ionicons/icons';
import { GeolocationService, getReverseGecodingAddress } from '../services/geolocation.service';

import SelectItem from './SelectItem';
import { handleErrors } from '../services/common.service';
import { useLocation } from 'react-router';
import ImageViewer from './ImageViewer';
import { dataUrlToFile, takePicture } from '../hooks/common';
import { uploadFile } from '../services/image.service';
import useImageUpload from '../hooks/useImageUpload';
import ImageUploadButton from './ImageUploadButton';
import { useHistory } from 'react-router';
import { useRecoilValue } from 'recoil';
import { userProfileState } from '../services/state.service';
import useUserDataStore from '../store/userDataStore';
import { addLead } from '../services/lead.service';
import UserList from '../pages/UserList';
import { useGenericLocalDataStore } from '../store/useGenericLocalDataStore';
import countryCodes from '../country_code.json';


interface PlanOption {
    id: string;
    description: string;
}

const groupOptions = [
    { id: 'agronomy', description: 'Agronomy' },
    { id: 'sales', description: 'Sales' },
    { id: 'marketing', description: 'Marketing' },
    { id: 'service', description: 'Service' }
];

// Activity types that show the optional "User IDs or Mobile Number" block
const ACTIVITY_TYPES_WITH_OPTIONAL_CONTACT = [
    "Online meetings/calls",
    "Installation",
    "Uninstallation",
    "Maintenance",
    "Testimonials",
    "Retailer/Distributor visits"
];


const CreateSalesActivity = ({salesActivity}: {salesActivity: any}) => {

    const location = useLocation();
    const history = useHistory();
    
    // Generic store for user preferences
    const { getItem, setItem: saveToStore } = useGenericLocalDataStore();
    const [acitivitesOptions, setActivitesOptions] = useState<PlanOption[]>([]);
    const [showActivities, setShowActivities] = useState(false);
    const [attachments, setAttachments] = useState({
        images: [] as string[],
        thumbnails: [] as string[]
    });
    const [activityType, setActivityType] = useState<PlanOption>();
    const [group, setGroup] = useState<string>('');
    const [showGroupModal, setShowGroupModal] = useState(false);
    const [groupTouched, setGroupTouched] = useState(false);
    const [date, setDate] = useState({date: new Date().toISOString()});
    
    // GPS and Location related states
    const [isGettingLocation, setIsGettingLocation] = useState(false);
    const [gpsEnabled, setGpsEnabled] = useState<boolean | null>(null);
    const [locationError, setLocationError] = useState<string>('');
    const [gpsLocation, setGpsLocation] = useState<string>('');
    const [gpsCoordinates, setGpsCoordinates] = useState<{ latitude: number; longitude: number } | null>(null);
    const [state, setState] = useState<string>('');
    const [district, setDistrict] = useState<string>('');
    const [taluka, setTaluka] = useState<string>('');
    const [comment, setComment] = useState('');
    const [userIds, setUserIds] = useState<any[]>([]);
    const [userid, setUserid] = useState<string>('');
    const [quantity, setQuantity] = useState(1);
    const [followUpDate, setFollowUpDate] = useState<string>('');

    
    
    const [onSuccess, setOnSuccess] = useState('');
    const [onError, setOnError] = useState('');
    const [disabled, setDisabled] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [lastSubmitTime, setLastSubmitTime] = useState(0);
    const [meetingProps, setMeetingProps] = useState<{[key:string]: any}>(
        {
         personPresent: 0,
         devicesSold: 0, 
         hotLeads: 0,
         
        });
    const userProfile: any = useRecoilValue(userProfileState);
    const { isPrivilegeUser } = useUserDataStore();
    const [showDeleteAlert, setShowDeleteAlert] = useState(false);
    
    // Image upload hook
    const { isUploading: isUploadingImage, uploadImage } = useImageUpload({
        primaryId: 'salesActivity',
        secondaryId: activityType?.description?.replaceAll(" ", "").replaceAll("/", "_") || 'default',
        validateBeforeUpload: () => {
            if (!activityType?.description) {
                return 'Please select an activity type before uploading images';
            }
            return true;
        },
        onSuccess: (responseBody) => {
            setAttachments({ 
                images: [...attachments.images, responseBody.images[0]], 
                thumbnails: [...attachments.thumbnails, responseBody.thumbnails[0]] 
            });
        },
        onError: (error) => {
            setOnError(error);
        }
    });
    
    const getDefaultFollowUpDate = () => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 15);
        return tomorrow.toISOString();
    };

    const [leadProps, setLeadProps] = useState<{[key:string]: any}>(
        {
            account: 'lead',
            contactName: '', 
            contactPhoneNumber: '',
            countryCode: '+91',
            followUpDate: getDefaultFollowUpDate(),
        }
    );


    const meetingPropsDict:{[key:string]: string} = {
        personPresent: 'No. of persons present',
         devicesSold: 'No. of devices sold', 
         hotLeads: 'No. of Hot leads',
    }

    const [selectedImage, setSelectedImage] = useState('');
    const [servicedBy, setServicedBy] = useState(userProfile.username);
    const [showImageModal, setShowImageModal] = useState(false);
    const [showSelectUserModal, setShowSelectUserModal] = useState(false);
    const [servicer, setServicer] = useState(false);
    const [showSelectCountryCodeModal, setShowSelectCountryCodeModal] = useState(false);
    
    useEffect(() => {
        let mounted = true;
        getAllSalesActivities().then((res) => setActivitesOptions(res));
        mounted = false;
    }, [])

    // Check GPS status and location permission
    const checkGPSStatus = async () => {
        try {
            const geoService = new GeolocationService();
            
            // Check if GPS is enabled
            const isGPSEnabled = await geoService.isGPSEnabled();
            setGpsEnabled(isGPSEnabled);
            
            if (!isGPSEnabled) {
                setLocationError('GPS is not enabled or location access denied');
                return;
            }
            
            await getCurrentLocation();
        } catch (error: any) {
            setGpsEnabled(false);
            setLocationError('Failed to check GPS status: ' + (error.message || 'Unknown error'));
        }
    };

    // Get current location with reverse geocoding
    const getCurrentLocation = async () => {
        setIsGettingLocation(true);
        setLocationError('');
        
        try {
            const geoService = new GeolocationService();
            const position = await geoService.getCurrentPosition();
            
            if (!position?.coords) {
                throw new Error('Failed to get location coordinates');
            }

            const { latitude, longitude } = position.coords;
            
            // Store GPS coordinates
            setGpsCoordinates({ latitude, longitude });
                        
            // Get reverse geocoding data
            try {
                const reverseGeocodeData = await getReverseGecodingAddress(latitude, longitude);
                if (reverseGeocodeData && reverseGeocodeData.display_name) {
                    const address = reverseGeocodeData.display_name;
                    setGpsLocation(address);
                    
                    // Set state, district, and taluka from reverse geocoding data
                    if (reverseGeocodeData.address) {
                        setState(reverseGeocodeData.address.state || '');
                        setDistrict(reverseGeocodeData.address.district || '');
                        setTaluka(reverseGeocodeData.address.subDistrict || '');
                    }
                }
            } catch (geocodeError) {
                console.error('Reverse geocoding failed:', geocodeError);
                setGpsLocation(`${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
            }

            setGpsEnabled(true);
            setLocationError(''); // Clear any previous errors
            
        } catch (error: any) {
            console.log('Location error:', error);
            
            // Enhanced error handling with user guidance
            if (error.code === 1) {
                setLocationError('Location permission denied. Please enable location access in your device settings.');
                setGpsEnabled(false);
            } else if (error.code === 2) {
                setLocationError('GPS signal weak or blocked. Try moving to an open area, near a window, or outdoors. Check if airplane mode is off.');
                // Don't disable GPS on weak signal - user can try again
                setGpsEnabled(true);
            } else if (error.code === 3) {
                // For timeout, just show a warning but don't disable GPS
                setLocationError('GPS signal weak. Please move to an open area or try again in a few seconds.');
                // Keep GPS enabled but show warning
                setGpsEnabled(true);
            } else {
                setLocationError('Failed to get location. Please check your GPS settings and try again.');
                setGpsEnabled(false);
            }
        } finally {
            setIsGettingLocation(false);
        }
    };

    useEffect(() => {
        setQuantity(1);
    }, [activityType]);

    useEffect(() => {
        // Only auto-fetch GPS location for new activities, not when editing existing ones
        if (!salesActivity) {
            checkGPSStatus();
        }
    }, [salesActivity]);
    
    // Load saved group preference on component mount
    useEffect(() => {
        const loadSavedGroup = async () => {
            const savedGroup = await getItem<string>('lastSelectedGroup');
            if (!salesActivity && savedGroup) {
                setGroup(savedGroup);
            }
        };
        loadSavedGroup();
    }, [getItem, salesActivity]);

    useEffect(() => {
        
        if (!!salesActivity) {
            setActivityType(salesActivity.salesActivityType);
            setGroup(salesActivity.group || '');
            setGroupTouched(true);
            setGpsLocation(salesActivity.place || salesActivity.location || '');
            if (salesActivity.location && typeof salesActivity.location === 'object' && salesActivity.location.coordinates) {
                setGpsCoordinates({
                    latitude: salesActivity.location.coordinates[1],
                    longitude: salesActivity.location.coordinates[0]
                });
            } else if (salesActivity.gpsCoordinates) {
                setGpsCoordinates(salesActivity.gpsCoordinates);
            }
            setState(salesActivity.state || '');
            setDistrict(salesActivity.district || '');
            setTaluka(salesActivity.taluka || '');
            setDate({ date: new Date(salesActivity.date).toISOString() });
            setComment(salesActivity.comment || '');
            setUserIds(salesActivity.userIds || []);
            setUserid(salesActivity.userid || '');
            setServicedBy(salesActivity.servicedBy || userProfile.username);
            setAttachments(salesActivity.attachments || { images: [], thumbnails: [] });
            setMeetingProps(salesActivity.meetingProps || { personPresent: 0, devicesSold: 0, hotLeads: 0 });
            setFollowUpDate(salesActivity.followUpDate || '');
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            setLeadProps(salesActivity.leadProps || {
                account: 'lead',
                contactName: '',
                contactPhoneNumber: '',
                countryCode: '+91',
                followUpDate: tomorrow.toISOString(),
            });
        }
    }, []);

    useEffect(() => {
        if (!salesActivity) {
            const prefill = (location?.state as any)?.prefillActivity;
            if (prefill) {
                setGroup(prefill.group || '');
                if (prefill.group) setGroupTouched(true);
                setUserIds(prefill.userIds || []);
                setLeadProps(prev => ({
                    ...prev,
                    contactPhoneNumber: prefill.contactPhoneNumber || '',
                    countryCode: prefill.countryCode || '+91',
                }));
            }
        }
    }, [location.state]);


    function setUserId(item: any) {
        if (!userIds.includes(item.farmUserId)) {
            setUserIds([...userIds, item]);
        }
        setShowSelectUserModal(false);
    }

    function removeUserId(idToRemove: string) {
        setUserIds(userIds.filter(id => id.farmUserId !== idToRemove));
    }

    function setServicedById(item: any) {
        setServicedBy(item.farmUserId);
        setShowSelectUserModal(false);
    }

    function setCountryCode(item: any) {
        setLeadProps({ ...leadProps, countryCode: item.dial_code });
        setShowSelectCountryCodeModal(false);
    }

    // Function to clean phone number by removing spaces, dashes, and other non-numeric characters
    const cleanPhoneNumber = (value: string): string => {
        if (!value) return '';
        // Remove spaces, dashes, parentheses, and other non-numeric characters except digits
        return value.replace(/[^\d]/g, '');
    }

    const onContactNumberChange = (value: string) => {
        const cleanedNumber = cleanPhoneNumber(value);
        setLeadProps({ ...leadProps, contactPhoneNumber: cleanedNumber });
    }


    function deleteImage(url: string) {
        const imageIndex = attachments.images.findIndex(item => item === url);
        if (imageIndex > -1) {
            attachments.images.splice(imageIndex, 1);
            attachments.thumbnails.splice(imageIndex, 1);
        }
        setAttachments({ images: [...attachments.images], thumbnails: [...attachments.thumbnails] });
    }

    const saveLead = async (values: any) => {
        try {
            values.servicedBy = userProfile.username || values.servicedBy;
            values.date = date.date;
            const response = await addLead(values).then(handleErrors);
            setOnSuccess('Lead details added'); 
        } catch (err: any) {
            console.log(err);
            setOnError(err);
        }
    };

 
    const handleBack = () => {
        history.replace('/tabs/salesPlanList', { reload: true });
        history.goBack();
    }

    const handleUpdate = async () => {
         const activity = {
            place: gpsLocation,
            location: gpsCoordinates ? {
              type: "Point",
              coordinates: [gpsCoordinates.longitude, gpsCoordinates.latitude]
            } : null,
            state: state,
            district: district,
            taluka: taluka,
            attachments,
            monthlySalesPlanId: (location?.state as any)?.planId,
            userIds: userIds?.map((item: any) => item.farmUserId),
            userid: '',
            comment: comment,
            date: new Date(date.date),
            salesActivityTypeId: activityType?.id,
            servicedBy: servicedBy,
            meetingProps: meetingProps,
            leadProps: leadProps,
            group: group,
            followUpDate: followUpDate || null,
          };
          const response = await updateSalesActivity(
            salesActivity.id,
            activity
          ).then(handleErrors);
          setOnSuccess("Sales Activity Updated");
          handleBack();
    }

    const handleAdd = async () => {
        var salesActivities = [];
            for (let index = 0; index < quantity; index++) {
              const activity = {
                place: gpsLocation,
                location: gpsCoordinates ? {
                  type: "Point",
                  coordinates: [gpsCoordinates.longitude, gpsCoordinates.latitude]
                } : null,
                state: state,
                district: district,
                taluka: taluka,
                attachments,
                monthlySalesPlanId: (location?.state as any)?.planId,
                userIds: userIds?.map((item: any) => item.farmUserId),
                comment: comment,
                date: new Date(date.date),
                salesActivityTypeId: activityType?.id,
                servicedBy: servicedBy,
                meetingProps: meetingProps,
                leadProps: leadProps,
                group: group,
                followUpDate: followUpDate || null,
              };
              salesActivities.push(activity);
            }

            const response = await addSalesActivity(salesActivities).then(
              handleErrors
            );
            const sourceActivityId = (location?.state as any)?.sourceActivityId;
            if (sourceActivityId) {
                await updateSalesActivity(sourceActivityId, { followUpDate: null });
            }
            setOnSuccess("Sales Activity Added");
            handleBack();
    }

    const submit = async () => {
      const currentTime = Date.now();
      
      // Prevent multiple submissions with multiple checks
      if (disabled || isSubmitting) {
        return;
      }
      
      // Debounce: prevent submissions within 2 seconds of each other
      if (currentTime - lastSubmitTime < 2000) {
        return;
      }
      
      setLastSubmitTime(currentTime);
      setDisabled(true);
      setIsSubmitting(true);
      
      try {
        if (!activityType?.id?.length) {
            setOnError("Some error occurred");
            return;
          }

        const isUpdate = !!salesActivity?.id;
        await (isUpdate ? handleUpdate() : handleAdd());

      } catch (err: any) {
        setOnError(err);
        console.log(err);
      } finally {
        setDisabled(false);
        setIsSubmitting(false);
      }
    };


    const deleteActivity = async () => {
        try {
            await deleteSalesActivity(salesActivity.id).then(handleErrors);
            setOnSuccess("Sales Activity Deleted");
            handleBack();
        } catch (err: any) {
            setOnError(err);
        }
    };

    const [lessPresent, setLessPresent] = useState(false);
    const [morePresent, setMorePresent] = useState(false);


    useEffect(() => {
        if(group === '') setDisabled(true);
        else if(!gpsLocation || !gpsCoordinates) setDisabled(true);
        else if(activityType?.description === undefined) setDisabled(true);
        
        else if(activityType?.description === "Personal Meetings - New Customers"){
            if(leadProps.contactName === '') setDisabled(true);
            else if(leadProps.contactPhoneNumber === '') setDisabled(true);
            else if(leadProps.countryCode === '') setDisabled(true);
            else if(leadProps.followUpDate === '') setDisabled(true);
            else setDisabled(false);
        }
        else if(activityType?.description==="Small Group Meetings (Less than 25)" || activityType?.description==="Large Group Meetings (More than 25)"){
            if(meetingProps['personPresent'] >25 && activityType?.description==="Small Group Meetings (Less than 25)"){
                setMorePresent(true);
                setDisabled(true);
            }   
            else if(meetingProps['personPresent'] <=25 && activityType?.description==="Large Group Meetings (More than 25)"){
                setLessPresent(true);
                setDisabled(true);
            }   
            else{
                setLessPresent(false);
                setMorePresent(false);
                setDisabled(false);
            }
        }
        else if(activityType?.description==="Personal Meetings - Existing Customers"){
            if(userIds.length === 0) setDisabled(true);
            else setDisabled(false);
        }
        else setDisabled(false);
    }, [userIds, meetingProps, leadProps, activityType, group, gpsLocation]);


    return (
      <>
        <IonItem>
          <IonLabel position="stacked">Group</IonLabel>
                            <IonInput
                    clearInput
                    readonly
                    id="group"
                    name="group"
                    value={groupOptions.find(g => g.id === group)?.description || ''}
                    placeholder="Select Group"
                    onClick={(e) => {
                      setGroupTouched(true);
                      setShowGroupModal(true);
                    }}
                  ></IonInput>
        </IonItem>
        {groupTouched && group === "" && (
          <div className="errorMsg">Required</div>
        )}
       
        <IonItem>
          <IonLabel position="stacked">Activity Type</IonLabel>
          <IonInput
            clearInput
            readonly
            id="activityType"
            name="activityType"
            value={activityType?.description}
            placeholder="Select Activity Type"
            disabled={!!salesActivity}
            onClick={(e) => setShowActivities(true)}
          ></IonInput>
        </IonItem>
        <IonItem>
          <IonLabel position="stacked"> Date </IonLabel>
          <IonDatetime
            name="activityDate"
            value={date.date}
            onIonChange={(e) => setDate({ date: e.detail.value ?? "" })}
          ></IonDatetime>
        </IonItem>
        {activityType?.description === "Personal Meetings - Existing Customers" && (
  <>
    <IonItem lines="none">
      <IonLabel position="stacked">User Ids</IonLabel>
    </IonItem>

    <div
      style={{
        width: "100%",
        padding: "0px 8px", 
        cursor: "pointer"
      }}
      onClick={() => {
        setShowSelectUserModal(true);
        setServicer(false);
      }}
    >
      {userIds.length === 0 ? (
        <div style={{ paddingLeft: "8px", color: "#888" }}>
          Select User Ids
        </div>
      ) : (
        <IonGrid style={{ padding: 0 }}>
          <IonRow>
            {userIds?.map((item, index) => (
              <IonCol size="auto" key={index}>
                <IonChip outline={true} color="primary">
                  <IonLabel>{item?.name || item?.farmUserId}</IonLabel>
                  <IonIcon
                    icon={closeCircle}
                    onClick={(e) => {
                      e.stopPropagation(); 
                      removeUserId(item?.farmUserId);
                    }}
                    style={{ cursor: "pointer" }}
                  />
                </IonChip>
              </IonCol>
            ))}
          </IonRow>
        </IonGrid>
      )}
    </div>

    {userIds.length === 0 && (
      <div className="errorMsg" style={{ marginLeft: 16, color: "red" }}>
        Required
      </div>
    )}
  </>
)}

        

        {activityType?.description === "Personal Meetings - New Customers" && (
          <IonList>
            <IonItem>
              <IonLabel position="stacked">Contact Name</IonLabel>
              <IonInput
                id="contactName"
                name="contactName"
                value={leadProps.contactName}
                placeholder="Contact Name"
                onIonChange={(e) =>
                  setLeadProps({ ...leadProps, contactName: e.detail.value! })
                }
              ></IonInput>
            </IonItem>
            {leadProps.contactName === "" && (
              <div className="errorMsg">Contact name is required</div>
            )}

            <IonItem>
              <IonLabel position="stacked">Country Code</IonLabel>
              <IonInput
                id="countryCode"
                name="countryCode"
                readonly
                value={leadProps.countryCode}
                placeholder="Country Code"
                onClick={(e) => setShowSelectCountryCodeModal(true)}
              ></IonInput>
            </IonItem>
            {leadProps.countryCode === "" && (
              <div className="errorMsg">Country code is required</div>
            )}

            <IonItem>
              <IonLabel position="stacked">Mobile Number</IonLabel>
              <IonInput
                id="contactPhoneNumber"
                name="contactPhoneNumber"
                type="tel"
                inputmode="tel"
                value={leadProps.contactPhoneNumber}
                placeholder="Mobile No. of the Contact"
                onIonChange={(e) => onContactNumberChange(e.detail.value || '')}
              ></IonInput>
            </IonItem>
            {leadProps.contactPhoneNumber === "" && (
              <div className="errorMsg">Phone number is required</div>
            )}

            <IonItem>
              <IonLabel position="stacked">Follow-up Date</IonLabel>
              <IonDatetime
                id="followUpDate"
                name="followUpDate"
                displayFormat="DD/MM/YYYY"
                placeholder="Follow-up Date"
                value={leadProps.followUpDate}
                onIonChange={(e) =>
                  setLeadProps({ ...leadProps, followUpDate: e.detail.value! })
                }
              ></IonDatetime>
            </IonItem>
            {leadProps.followUpDate === "" && (
              <div className="errorMsg">Follow-up date is required</div>
            )}
          </IonList>
        )}

        {ACTIVITY_TYPES_WITH_OPTIONAL_CONTACT.includes(activityType?.description ?? '') && (
          <>
            <IonItem lines="none">
              <IonLabel position="stacked">User Ids (Optional)</IonLabel>
            </IonItem>
            <div
              style={{ width: "100%", padding: "0px 8px", cursor: "pointer" }}
              onClick={() => { setShowSelectUserModal(true); setServicer(false); }}
            >
              {userIds.length === 0 ? (
                <div style={{ paddingLeft: "8px", color: "#888" }}>Select User Ids</div>
              ) : (
                <IonGrid style={{ padding: 0 }}>
                  <IonRow>
                    {userIds?.map((item, index) => (
                      <IonCol size="auto" key={index}>
                        <IonChip outline={true} color="primary">
                          <IonLabel>{item?.name || item?.farmUserId}</IonLabel>
                          <IonIcon
                            icon={closeCircle}
                            onClick={(e) => { e.stopPropagation(); removeUserId(item?.farmUserId); }}
                            style={{ cursor: "pointer" }}
                          />
                        </IonChip>
                      </IonCol>
                    ))}
                  </IonRow>
                </IonGrid>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', margin: '12px 16px' }}>
              <div style={{ flex: 1, height: '1px', background: '#ccc' }} />
              <span style={{ margin: '0 12px', color: '#888', fontSize: '13px', fontWeight: 600 }}>OR</span>
              <div style={{ flex: 1, height: '1px', background: '#ccc' }} />
            </div>

            <IonList>
              <IonItem>
                <IonLabel position="stacked">Country Code (Optional)</IonLabel>
                <IonInput
                  readonly
                  value={leadProps.countryCode}
                  placeholder="Country Code"
                  onClick={() => setShowSelectCountryCodeModal(true)}
                ></IonInput>
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Mobile Number (Optional)</IonLabel>
                <IonInput
                  type="tel"
                  inputmode="tel"
                  value={leadProps.contactPhoneNumber}
                  placeholder="Mobile No. of the Contact"
                  onIonChange={(e) => onContactNumberChange(e.detail.value || '')}
                ></IonInput>
              </IonItem>
            </IonList>
          </>
        )}

        {!salesActivity && activityType?.description === "Installation" && (
          <IonItem>
            <IonLabel position="stacked">Quantity</IonLabel>
            <IonInput
              clearInput
              type="number"
              id="quantity"
              name="quantity"
              value={quantity ?? ""}
              placeholder="Select Quantity"
              onIonChange={(e) => setQuantity(parseInt(e.detail.value ?? ""))}
            ></IonInput>
          </IonItem>
        )}

                        {/* GPS Status Indicator - Only show for new activities */}
                {!salesActivity && (
                    <>
                        <IonItem lines="none">
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                <IonChip 
                                    color={gpsEnabled ? 'success' : gpsEnabled === false ? 'danger' : 'warning'}
                                    outline
                                >
                                    <IonIcon icon={gpsEnabled ? locationSharp : locationOutline} />
                                    <IonLabel>
                                        {gpsEnabled ? 'GPS Enabled' : gpsEnabled === false ? 'GPS Disabled' : 'Checking GPS...'}
                                    </IonLabel>
                                </IonChip>
                                
                                {/* Retry Button for GPS Issues */}
                                {locationError && gpsEnabled !== false && (
                                    <IonButton 
                                        size="small" 
                                        fill="outline" 
                                        onClick={getCurrentLocation}
                                        disabled={isGettingLocation}
                                    >
                                        {isGettingLocation ? 'Getting...' : 'Retry GPS'}
                                    </IonButton>
                                )}
                            </div>
                        </IonItem>
                
                        {locationError && (
                            <div className="errorMsg" style={{ marginLeft: 16, color: 'red' }}>
                                {locationError}
                                {locationError.includes('GPS signal weak') && (
                                    <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                                        💡 <strong>Tips:</strong> Move to an open area, near a window, or outdoors. Ensure airplane mode is off.
                                    </div>
                                )}
                                {locationError.includes('permission denied') && (
                                    <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                                        💡 <strong>Solution:</strong> Go to Settings &gt; Privacy & Security &gt; Location Services and enable for this app.
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
        
        <IonItem>
          <IonLabel position="stacked">Location</IonLabel>
          <IonInput 
              disabled={true} 
              id="location" 
              name="location" 
              value={gpsLocation} 
              placeholder="Location will be set automatically from GPS" 
              className="location-input"
          ></IonInput>
        </IonItem>

        {(activityType?.description === "Small Group Meetings (Less than 25)" ||
          activityType?.description ===
            "Large Group Meetings (More than 25)") &&
          Object.keys(meetingProps).map((key, index) => {
            return (
              <>
                <IonItem key={index}>
                  <IonLabel position="stacked">
                    {meetingPropsDict[key]}
                  </IonLabel>
                  <IonInput
                    clearInput
                    type="number"
                    id={key}
                    name={key}
                    value={meetingProps[key] ?? 0}
                    placeholder={key}
                    onIonChange={(e) =>
                      setMeetingProps({
                        ...meetingProps,
                        [key]: parseInt(e.detail.value!),
                      })
                    }
                  ></IonInput>
                </IonItem>
                {lessPresent && key === "personPresent" && (
                  <div className="errorMsg">Persons less than 25</div>
                )}
                {morePresent && key === "personPresent" && (
                  <div className="errorMsg">Persons more than 25</div>
                )}
              </>
            );
          })}

            <IonItem>
                <IonLabel position="stacked">Serviced By</IonLabel>
                <IonInput readonly id="servicedBy" name="servicedBy" value={servicedBy} placeholder="Select Servicer" onClick={e => {setShowSelectUserModal(true); setServicer(true);}}></IonInput>
            </IonItem>

            <IonItem>
                <IonLabel position="stacked">Comment</IonLabel>
                <IonTextarea id="comment" name="comment" value={comment ?? ''} placeholder="Comment" onIonChange={e => setComment( e.detail.value ?? '' )}></IonTextarea>
            </IonItem>

            <IonItem>
                <IonLabel position="stacked">Follow-up Date (Optional)</IonLabel>
                <IonDatetime
                    displayFormat="DD/MM/YYYY"
                    placeholder="Set a follow-up reminder"
                    value={followUpDate || undefined}
                    onIonChange={e => setFollowUpDate(e.detail.value || '')}
                ></IonDatetime>
                {followUpDate && (
                    <IonIcon
                        icon={closeCircle}
                        slot="end"
                        style={{ cursor: 'pointer', color: '#888' }}
                        onClick={() => setFollowUpDate('')}
                    />
                )}
            </IonItem>

            
       
            <>
                <IonItem lines='none'>
                    <IonLabel>Images</IonLabel>
                    <ImageUploadButton
                        isUploading={isUploadingImage}
                        onUpload={uploadImage}
                    />
                </IonItem>
                <IonItem lines='none'>
                    <IonGrid>
                        <IonRow>
                            {attachments?.thumbnails?.map((item: any, index: number) =>
                                <IonCol key={index}>
                                    <IonThumbnail>
                                        <img src={item} alt='activityImage' onClick={e => {
                                            setSelectedImage(attachments?.images[index]);
                                            setShowImageModal(true);
                                        }} />
                                    </IonThumbnail>
                                </IonCol>
                            )}
                        </IonRow>
                    </IonGrid>
                </IonItem>
            </>

            <IonModal isOpen={showImageModal} cssClass='my-custom-class'>
                <ImageViewer imageUrl={selectedImage} onDeleteHandler={deleteImage} onCloseHandler={setShowImageModal} />
            </IonModal>

        <IonModal isOpen={showActivities}>
          <SelectItem
            label="Select Activity Type"
            selectedValue={activityType?.id ?? ""}
            labelKey="description"
            valueKey="id"
            setItem={(item: any) => {
              setActivityType(item);
              setShowActivities(false);
            }}
            onClose={() => setShowActivities(false)}
            items={acitivitesOptions}
          />
        </IonModal>

        <IonModal isOpen={showGroupModal}>
          <SelectItem
            label="Select Group"
            selectedValue={group ?? ""}
            labelKey="description"
            valueKey="id"
            setItem={(item: any) => {
              setGroup(item.id);
              saveToStore('lastSelectedGroup', item.id); // Save to generic store
              setShowGroupModal(false);
            }}
            onClose={() => setShowGroupModal(false)}
            items={groupOptions}
          />
        </IonModal>

        {showSelectUserModal && (
          <IonModal isOpen={showSelectUserModal}>
            <UserList
              selectedValue={servicer ? servicedBy : []}
              onClickHandler={servicer ? setServicedById : setUserId}
              onClose={setShowSelectUserModal}
              hiddenItems={!servicer ? userIds.map((id) => id.farmUserId) : []}
            />
          </IonModal>
        )}

        {showSelectCountryCodeModal && (
          <IonModal isOpen={showSelectCountryCodeModal}>
            <SelectItem
              label="Select Country"
              items={countryCodes}
              labelKey="name"
              valueKey="dial_code"
              setItem={setCountryCode}
              selectedValue=""
              onClose={() => setShowSelectCountryCodeModal(false)}
            />
          </IonModal>
        )}

        <IonButton
          disabled={disabled || isGettingLocation || isSubmitting || isUploadingImage}
          expand="block"
          type="submit"
          onClick={submit}
        >
         {(disabled || isSubmitting) ? "Save Activity" : 
          isUploadingImage ? "Uploading Image..." :
          (!!salesActivity?.id ? "Update Activity" : "Save Activity")}
        </IonButton>
        
        {!!salesActivity?.id && (isPrivilegeUser || salesActivity.servicedBy === userProfile?.username) && (
        <IonButton
          expand="block"
          color="danger"
          onClick={() => setShowDeleteAlert(true)}
        >
          Delete Activity
        </IonButton>
        )}

        <IonAlert
          isOpen={showDeleteAlert}
          onDidDismiss={() => setShowDeleteAlert(false)}
          header="Delete Activity"
          message="Are you sure you want to delete this activity? This cannot be undone."
          buttons={[
            { text: 'Cancel', role: 'cancel' },
            { text: 'Delete', role: 'destructive', handler: deleteActivity },
          ]}
        />

        <IonToast
          isOpen={!!onSuccess}
          onDidDismiss={() => setOnSuccess("")}
          message={onSuccess}
          duration={2000}
          color="success"
        />

        <IonToast
          isOpen={!!onError}
          onDidDismiss={() => setOnError("")}
          message={onError}
          duration={2000}
          color="danger"
        />
      </>
    );
};

export default CreateSalesActivity;


 